/**
 * Pharmacy portal client (PH-001). Its own token under its own key, never
 * attached to a clinic or patient request, for the same reason `portal.tsx`
 * keeps the patient's apart.
 */

import { ApiClientError } from './api';

const BASE = '/api/v1/pharmacy';
const ACCESS_KEY = 'mcos.pharmacy.access';

export const pharmacyToken = {
  get(): string | null {
    try {
      return localStorage.getItem(ACCESS_KEY);
    } catch {
      return null;
    }
  },
  set(token: string): void {
    try {
      localStorage.setItem(ACCESS_KEY, token);
    } catch {
      /* private window: the session lasts this tab */
    }
  },
  clear(): void {
    try {
      localStorage.removeItem(ACCESS_KEY);
    } catch {
      /* nothing stored */
    }
  },
};

const expiredListeners = new Set<() => void>();
export function onPharmacySessionExpired(listener: () => void): () => void {
  expiredListeners.add(listener);
  return () => expiredListeners.delete(listener);
}

async function toError(response: Response): Promise<ApiClientError> {
  try {
    const body = (await response.json()) as { error?: { code?: string; message?: string; details?: unknown } };
    return new ApiClientError(response.status, body.error?.code ?? 'UNKNOWN', body.error?.message ?? `Request failed with status ${response.status}`, body.error?.details);
  } catch {
    return new ApiClientError(response.status, 'UNKNOWN', `Request failed with status ${response.status}`);
  }
}

async function send(method: string, path: string, body?: unknown, anonymous = false): Promise<Response> {
  const headers = new Headers();
  const isForm = body instanceof FormData;
  if (body !== undefined && !isForm) headers.set('content-type', 'application/json');
  const token = pharmacyToken.get();
  if (!anonymous && token) headers.set('authorization', `Bearer ${token}`);
  const response = await fetch(`${BASE}${path}`, { method, headers, body: body === undefined ? undefined : isForm ? (body as FormData) : JSON.stringify(body) });
  if (response.status === 401 && !anonymous) {
    pharmacyToken.clear();
    for (const listener of expiredListeners) listener();
  }
  return response;
}

async function json<T>(method: string, path: string, body?: unknown, anonymous = false): Promise<T> {
  const response = await send(method, path, body, anonymous);
  if (!response.ok) throw await toError(response);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const pharmacyApi = {
  get: <T>(path: string) => json<T>('GET', path),
  post: <T>(path: string, body?: unknown) => json<T>('POST', path, body ?? {}),
  put: <T>(path: string, body?: unknown) => json<T>('PUT', path, body ?? {}),
  delete: <T>(path: string) => json<T>('DELETE', path),
  anonymous: <T>(path: string, body: unknown) => json<T>('POST', path, body, true),
  /** A file the signed-in pharmacy downloads: the template, the error list. */
  async download(path: string, fileName: string): Promise<void> {
    const response = await send('GET', path);
    if (!response.ok) throw await toError(response);
    const url = URL.createObjectURL(await response.blob());
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  },
};

export type PharmacyRole = 'admin' | 'support' | 'pricing';

export interface PharmacyMe {
  user: { id: string; name: string; email: string; role: PharmacyRole };
  pharmacy: {
    id: string;
    name: string;
    status: string;
    baaSigned: boolean;
    baaSignedOn: string | null;
    licenseVerified: boolean;
    replyTargetHours: number;
    escalateAfterHours: number;
    supportHoursText: string | null;
    mainPhone: string | null;
    website: string | null;
  };
  checklist: Array<{ key: string; label: string; done: boolean }>;
  clinics: { linked: number; requests: number };
}

export type SignInStep =
  | { step: 'mfa_setup'; mfaToken: string; secret: string; otpauthUrl: string }
  | { step: 'mfa'; mfaToken: string };

export const money = (cents: number | null | undefined) =>
  cents == null ? '-' : `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const shortDate = (iso: string | null | undefined) => {
  if (!iso) return '-';
  const d = new Date(iso.length === 10 ? `${iso}T12:00:00Z` : iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
};
